import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRightIcon, BookOpenIcon, LibraryIcon, StampIcon } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { ExamStamp } from "@/components/ledger/ExamStamp";
import { Badge } from "@/components/ledger/Badge";
import { useExams } from "@/lib/query-hooks";
import type { Exam } from "@/lib/types";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "PN-CivilServices — The Ledger for HPAS, GPSC, UPSC" },
      {
        name: "description",
        content:
          "Pick your exam. Every PYQ, NCERT, standard book and handwritten note — filed by subject and year, one calm archive.",
      },
      {
        property: "og:title",
        content: "PN-CivilServices — The Ledger for HPAS, GPSC, UPSC",
      },
      {
        property: "og:description",
        content:
          "Pick your exam. Every PYQ, NCERT, standard book and handwritten note — filed by subject and year, one calm archive.",
      },
    ],
  }),
  component: Home,
});

const FALLBACK_EXAMS: Exam[] = [
  {
    id: "upsc",
    slug: "upsc",
    name: "Union Public Service Commission",
    shortCode: "UPSC",
    description:
      "The national civil services examination — Prelims, Mains, Interview.",
    subjectCount: 24,
  },
  {
    id: "gpsc",
    slug: "gpsc",
    name: "Gujarat Public Service Commission",
    shortCode: "GPSC",
    description: "Class 1 & 2 combined services, Gujarat administrative posts.",
    subjectCount: 18,
  },
  {
    id: "hpas",
    slug: "hpas",
    name: "Himachal Pradesh Administrative Service",
    shortCode: "HPAS",
    description: "State services examination for Himachal Pradesh.",
    subjectCount: 16,
  },
];

function Home() {
  const { data, isLoading, isError } = useExams();
  const exams = (data && data.length ? data : FALLBACK_EXAMS) as Exam[];

  return (
    <AppShell>
      {/* Hero */}
      <section className="relative">
        <div className="mx-auto max-w-7xl px-4 pt-16 pb-14 sm:px-6 lg:px-8">
          <div className="label-card mb-6">A study ledger · Est. 2026</div>
          <h1 className="max-w-3xl font-display text-5xl md:text-6xl leading-[1.05] font-semibold tracking-tight">
            Every PYQ. Every NCERT. Every note.{" "}
            <span className="highlight">Filed.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-ink-soft leading-relaxed">
            PN-CivilServices is a calm, opinionated card-catalog for HPAS, GPSC
            and UPSC aspirants. Pick an exam, drill through subject and module,
            and find the exact file you were looking for — no infinite scroll,
            no dark patterns.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#exams"
              className="inline-flex items-center gap-2 rounded-md bg-paprika px-4 py-2.5 text-sm font-semibold uppercase tracking-widest text-white hover:opacity-90"
            >
              Choose your exam <ArrowRightIcon className="h-4 w-4" />
            </a>
            <Link
              to="/search"
              search={{ q: "" }}
              className="inline-flex items-center gap-2 rounded-md border border-hairline bg-surface px-4 py-2.5 text-sm font-semibold uppercase tracking-widest hover:bg-surface-alt"
            >
              Search the archive
            </Link>
          </div>
        </div>

        {/* Faint ledger lines */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-24"
          style={{
            backgroundImage:
              "repeating-linear-gradient(to bottom, transparent 0 22px, var(--border-hairline) 22px 23px)",
            maskImage:
              "linear-gradient(to bottom, transparent, black 60%)",
          }}
        />
      </section>

      {/* Exams */}
      <section
        id="exams"
        className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8"
      >
        <div className="mb-6 flex items-end justify-between">
          <div>
            <div className="label-card">Section 01 · Exams</div>
            <h2 className="mt-1 font-display text-3xl font-semibold">
              Open a drawer.
            </h2>
          </div>
          {isError ? (
            <div className="text-xs text-ink-muted">
              Showing preview — backend not reachable
            </div>
          ) : null}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="card-ledger corner-fold h-48 animate-pulse"
                >
                  <span className="corner-fold-inner" />
                </div>
              ))
            : exams.map((e) => <ExamCard key={e.id} exam={e} />)}
        </div>
      </section>

      {/* Modules explainer */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="label-card">Section 02 · How it's filed</div>
        <h2 className="mt-1 font-display text-3xl font-semibold">
          One archive, five drawers per subject.
        </h2>
        <div className="mt-8 grid gap-3 md:grid-cols-5">
          {[
            ["PYQs", "By year — 2010 · 2011 · … · 2024"],
            ["NCERTs", "By class — VI · VII · … · XII"],
            ["Standard Books", "Laxmikanth · Spectrum · Bipan Chandra"],
            ["Study Materials", "Curated coaching handouts & compilations"],
            ["Handwritten Notes", "Topper notes, subject-wise"],
          ].map(([title, sub]) => (
            <div
              key={title}
              className="card-ledger p-4 hover:border-paprika transition-colors"
            >
              <div className="flex items-center gap-2 text-paprika">
                <BookOpenIcon className="h-4 w-4" />
                <span className="label-card text-paprika">{title}</span>
              </div>
              <div className="mt-2 text-sm text-ink-soft">{sub}</div>
            </div>
          ))}
        </div>
      </section>
    </AppShell>
  );
}

function ExamCard({ exam }: { exam: Exam }) {
  return (
    <Link
      to="/exam/$examSlug"
      params={{ examSlug: exam.slug }}
      className="group card-ledger corner-fold relative flex h-full flex-col p-5 hover:border-paprika transition-colors"
    >
      <span className="corner-fold-inner" />
      <div className="flex items-start justify-between gap-3">
        <ExamStamp code={exam.shortCode} />
        <Badge variant="outline">
          <LibraryIcon className="h-3 w-3" />
          {exam.subjectCount ?? 0} subjects
        </Badge>
      </div>
      <h3 className="mt-6 font-display text-xl font-semibold leading-snug">
        {exam.name}
      </h3>
      <p className="mt-2 text-sm text-ink-soft line-clamp-2">
        {exam.description}
      </p>
      <div className="mt-auto pt-5 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-paprika text-xs font-semibold uppercase tracking-widest">
          <StampIcon className="h-3.5 w-3.5" />
          Open drawer
        </div>
        <ArrowRightIcon className="h-4 w-4 text-ink-muted group-hover:text-paprika group-hover:translate-x-0.5 transition" />
      </div>
    </Link>
  );
}
